import { create } from 'zustand';

/**
 * A takeover of a live AI call, from the moment `takeOverAiCall` succeeds until the AI has left.
 *
 * - `requested`: the member's device is being dialled.
 * - `handingOver`: the member answered; the AI says its hand-off line while the member stays muted on
 *   the Twilio side, so they cannot be heard yet whatever the local mic shows.
 *
 * It ends when the live call's agent becomes a human (the AI left), or when the takeover leg drops.
 */
export type AiTakeoverPhase = 'requested' | 'handingOver';

type AiTakeover = {
  messageConversationId: number;
  supervisionId: string;
  phase: AiTakeoverPhase;
};

type AiTakeoverStore = {
  takeover: AiTakeover | null;
  startTakeover: (messageConversationId: number, supervisionId: string) => void;
  markAnswered: (supervisionId: string | undefined) => void;
  resetTakeover: VoidFunction;
};

export const useAiTakeoverStore = create<AiTakeoverStore>((set, get) => ({
  takeover: null,
  startTakeover: (messageConversationId, supervisionId) =>
    set({ takeover: { messageConversationId, supervisionId, phase: 'requested' } }),
  markAnswered: (supervisionId) => {
    const { takeover } = get();
    // The incoming leg carries the same supervisionId as the mutation response.
    if (!takeover || (supervisionId && takeover.supervisionId !== supervisionId)) return;
    set({ takeover: { ...takeover, phase: 'handingOver' } });
  },
  resetTakeover: () => set({ takeover: null }),
}));
